<template>
  <div class="package-table" :style="{ marginTop: presetKitId ? '25px' : '5px' }">
    <div class="row q-gutter-md q-mb-md items-end">
      <div class="col d-flex justify-end" style="text-align: end;">
        <CustomButton class="createBtn" @click="handleAddClick">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <span>Добавить конструктив</span>
            <q-icon name="img:img/icons/add_box.svg" size="16px" />
          </div>
        </CustomButton>
      </div>
    </div>
    <CustomTable :title="tableTitle" :rowsData="data" :columns="constructivesColumns" :loading="isFetching"
      :custom-body="true" :small="true" :rowsDataGroupMode="true" :groupProps="{
        groupNameProp: 'group_name', groupChildrenProp: 'work_sets', childrenKeys: [
          'work_set_id',
          'work_set_name',
          'material_count',
          'unit_short_name',
        ],
      }">
      <template #customBody="props">
        <q-tr class="groupWrapper">
          <q-td :colspan="constructivesColumns.length - 1" class="group">
            {{ props.row.group_name }}
          </q-td>
          <q-td class="group group__actions-cell">
            <CustomIcon icon="edit" title="Редактировать" @click.stop="openEditGroupModal(props.row)" />
          </q-td>
        </q-tr>
        <q-tr v-for="(item, i) in props.row.work_sets">
          <custom-table-cell align="center" vertical-align="center">
            {{ item.work_set_id }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ item.work_set_name }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <span>{{ item.formula }}</span>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <span>{{ item.unit_short_name }}</span>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center" style="width: 150px;">
            <div v-if="item.work_set_price" class="provider-info">
              <CustomPopup width="1000" :noPadding="true">
                <template #head>
                  <Clickable>
                    {{ numberWithCommas(item.work_set_price) }}
                  </Clickable>
                </template>
                <template #default>
                  <ConstructivePriceTable :contractor_work_set_id="item.contractor_work_set_id" @refresh-data="refreshData" />
                </template>
              </CustomPopup>
            </div>
            <div v-else>
              <CustomButton style="height: 32px; width: auto; font-size: 11px;" @click="onAddPriceClick(item)">
                <span>Добавить цену</span>
              </CustomButton>
            </div>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center" style="width: 100px; min-width: 100px;">
            <div style="display: flex; justify-content: center; align-items: center; gap: 8px;">
              <CustomIcon icon="edit" title="Редактировать" @click="handleEditClick(item)" />
              <CustomIcon icon="trash" title="Удалить" :approve-mode="true" :approve="() => handleDeleteClick(item)" />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomIcon from '@/components/common/CustomIcon';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import {
  getConstructivesList,
  constructivesColumns,
  addConstructivesPrice,
  addConstructive,
  updConstructive,
  removeConstructiveFromProvider
} from './services';
import { MODAL_NAMES } from '@/constants';
import { useAppStore } from '@/services/store/app';
import ConstructivePriceTable from './ConstructivePriceTable.vue';
import Clickable from '@/components/common/Clickable';
import CustomPopup from '@/components/common/CustomPopup';
import { numberWithCommas } from '@/services';

const props = defineProps({
  presetKitId: {
    type: Number,
    required: false
  },
  title: {
    type: String,
    default: ''
  },
  projectId: {
    type: Number,
  },
})
const { showSucceedSnackbar } = useCustomSnackbar();
const appStore = useAppStore()
const isFetching = ref(false)
const data = ref([])
const tableTitle = computed(() => props.title)

const getData = async () => {
  isFetching.value = true
  const res = await getConstructivesList({ preset_kit_id: props.presetKitId })
  isFetching.value = false
  data.value = res || []
}

useQuery({
  queryKey: ['materials', props.projectId || props.presetKitId],
  queryFn: () => getData(),
  enabled: computed(() => !!props.projectId || !!props.presetKitId)
})

function handleEditClick(row) {
  appStore.showModal(MODAL_NAMES.EditConstructiveSet, {
    title: 'Редактировать конструктив',
    onSubmit: body => editConstructive(body),
    body: row
  })
}

async function editConstructive(body) {
  isFetching.value = true
  const res = await updConstructive(body)
  isFetching.value = false
  if (!res) return
  showSucceedSnackbar()
  data.value = res
  appStore.closeModal()
}

function handleAddClick() {
  appStore.showModal(MODAL_NAMES.AddConstructive, {
    title: 'Добавить конструктив',
    onSubmit: addNewConstructive,
    body: {
      preset_kit_id: props.presetKitId
    }
  })
}

async function addNewConstructive(body) {
  isFetching.value = true
  const res = await addConstructive(body)
  isFetching.value = false
  if (!res) return
  showSucceedSnackbar()
  data.value = res
  appStore.closeModal()
}

async function handleDeleteClick(item) {
  isFetching.value = true
  const res = await removeConstructiveFromProvider(item.contractor_work_set_id, { preset_kit_id: props.presetKitId })
  isFetching.value = false
  if (!res) return
  showSucceedSnackbar()
  data.value = res
  appStore.closeModal()
}
function onAddPriceClick(item) {
  appStore.showModal(MODAL_NAMES.AddConstructivePrice, {
    title: 'Добавить цену',
    onSubmit: handlePriceAdd,
    body: { contractor_work_set_id: item.contractor_work_set_id }
  })
}
async function handlePriceAdd(body) {
  isFetching.value = true;
  const res = await addConstructivesPrice(body);
  isFetching.value = false;
  if (!res) {
    return;
  }
  showSucceedSnackbar();
  refreshData();
  appStore.closeModal();
}
function refreshData() {
  getData();
}

function openEditGroupModal(group) {
  appStore.showModal(MODAL_NAMES.EditWorkSetGroup, {
    title: 'Редактировать группу работ',
    group: {
      group_id: group.group_id,
      group_name: group.group_name,
      is_asr: group.is_asr
    },
    onSuccess: () => getData()
  })
}
</script>

<style lang="scss" scoped>
.groupWrapper {
  position: sticky;
  top: 48px;
  z-index: 49;
}

.group {
  padding: 10px 15px !important;
  background-color: #dde9ff !important;
  font-weight: bold;
  font-size: 14px;
  color: #0a1b3a;
}
.group__actions-cell {
  width: 100px;
  min-width: 100px;
  text-align: center;
  vertical-align: middle;
}

.package-table {
  margin-top: 25px;

  .table-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    .row {
      margin-right: 16px;
    }
  }

  .createBtn {
    width: auto;
    font-weight: 400;
    height: 44px;
  }
}
</style>
